/**
 * CBAM Module — Carbon Border Adjustment Mechanism (definitive regime, Reg. (EU) 2023/956)
 * Route: /frameworks/cbam
 *
 * Mental model: "Am I over the 50t threshold, what will certificates cost, and is my
 * annual declaration defensible?" Backed by /api/v1/cbam/* (import capture with evidence,
 * verified emission factors, 50t tracking, cost forecast, annual declaration workspace).
 *
 * Visual system: DESIGN.md — deep obsidian, JetBrains Mono for every data value,
 * status by color + icon + label (auditors print in grayscale). No decorative chrome.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ShieldCheck, Gauge, Coins, FileCheck2, Upload, Plus, Clock,
  AlertTriangle, CheckCircle2, CircleDashed, Package, RefreshCw, X,
} from "lucide-react";

// ─── DESIGN.md tokens (Command Center / Defensibility Spine) ───────────────────
const C = {
  bg: "#0A0A0A", surface: "#171717", surfaceHi: "#1F1F22", border: "#27272A",
  primary: "#10B981", accent: "#F59E0B", destructive: "#EF4444", locked: "#3B82F6",
  ink: "#FAFAFA", body: "#A1A1AA", muted: "#52525B", neutral: "#71717A",
};
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";
const HEAD = "'Outfit', 'Inter', system-ui, sans-serif";
const REPORTING_YEAR = 2026; // Phase-1 hero: 2026 imports (first annual declaration due 30 Sep 2027)

const API_BASE =
  typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL
    ? process.env.REACT_APP_BACKEND_URL
    : "";

async function apiFetch(path, opts = {}) {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  const isForm = opts.body instanceof FormData;
  const r = await fetch(`${API_BASE}${path}`, {
    method: opts.method || "GET",
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? (isForm ? opts.body : JSON.stringify(opts.body)) : undefined,
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      detail = (await r.json())?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(typeof detail === "string" ? detail : "Request failed");
  }
  return r.json();
}

// ─── Formatting (data always in mono) ──────────────────────────────────────────
const fmt = (n, d = 3) =>
  n === null || n === undefined || n === "" || isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString(undefined, {
        minimumFractionDigits: d === 0 ? 0 : Math.min(d, 2),
        maximumFractionDigits: d,
      });
const eur = (n) =>
  n === null || n === undefined ? null : `€${fmt(n, 2)}`;

const Data = ({ children, color, size = 12, weight = 600 }) => (
  <span style={{ fontFamily: MONO, fontSize: size, fontWeight: weight, color: color || C.ink }}>
    {children}
  </span>
);

// ─── Status vocabularies (color + icon + label; never color alone) ─────────────
const DECL = {
  open: { c: C.neutral, Icon: CircleDashed, label: "Open" },
  draft: { c: C.accent, Icon: FileCheck2, label: "Draft" },
  ready: { c: C.locked, Icon: ShieldCheck, label: "Ready" },
  submitted: { c: C.primary, Icon: CheckCircle2, label: "Submitted" },
};
const THRESH = {
  below: { c: C.primary, Icon: CheckCircle2, label: "Below 50t" },
  warning: { c: C.accent, Icon: AlertTriangle, label: "Approaching" },
  crossed: { c: C.destructive, Icon: AlertTriangle, label: "In scope" },
};
// How the free-allocation adjustment (Reg. 2025/2620) was derived — a material
// assurance disclosure, shown per line so an auditor sees it without drilling in.
const BASIS = {
  exact: { c: C.primary, Icon: CheckCircle2, label: "Exact" },
  exact_conservative_route: { c: C.accent, Icon: ShieldCheck, label: "Conservative route" },
  approximation_no_benchmark: { c: C.neutral, Icon: CircleDashed, label: "Approx · no benchmark" },
};
const BASIS_NOTE = {
  exact: "Free-allocation adjustment used the CN code's benchmark directly.",
  exact_conservative_route:
    "Benchmark route was ambiguous for this CN code; the lowest in-scope benchmark was applied (smallest deduction — never understates liability).",
  approximation_no_benchmark:
    "No CN-code benchmark applies; liability floored at embedded × CBAM factor.",
};

// EF provenance for one calc: source, origin-country default, and the mandatory
// Reg. 2025/2621 year mark-up. Chip stays legible in grayscale (icon + text).
function EFProvenance({ calc }) {
  if (!calc) return <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>no factor resolved</span>;
  const isOp = calc.ef_source === "operator_actual";
  const c = isOp ? C.primary : C.accent;
  const mult = Number(calc.markup_multiplier);
  const markPct = !isOp && mult > 1 ? ` · +${Math.round((mult - 1) * 100)}%` : "";
  const country =
    calc.country_code === "WLD" ? "RoW default"
      : calc.country_code ? `${calc.country_code} default`
      : "Commission default";
  const label = isOp ? "operator actual" : `${country}${markPct}`;
  return (
    <span
      title={`${calc.ef_source} · ${calc.ef_version}${markPct ? ` · mark-up ${calc.markup_multiplier}` : ""}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 999, background: `${c}14`, border: `1px solid ${c}40` }}
    >
      <ShieldCheck size={11} strokeWidth={1.5} color={c} />
      <span style={{ fontFamily: MONO, fontSize: 10, color: c, whiteSpace: "nowrap" }}>{label}</span>
    </span>
  );
}
const BasisBadge = ({ basis }) => {
  const s = BASIS[basis];
  if (!s) return <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>—</span>;
  return (
    <span title={BASIS_NOTE[basis]} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: MONO, fontSize: 10, color: s.c, whiteSpace: "nowrap" }}>
      <s.Icon size={11} strokeWidth={1.5} /> {s.label}
    </span>
  );
};

const Badge = ({ c, Icon, label }) => (
  <span
    style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
      borderRadius: 999, fontFamily: MONO, fontSize: 11, fontWeight: 600,
      color: c, background: `${c}1A`, border: `1px solid ${c}40`, whiteSpace: "nowrap",
    }}
  >
    {Icon && <Icon size={12} strokeWidth={1.5} />} {label}
  </span>
);

const Card = ({ children, style, accent }) => (
  <section
    style={{
      background: C.surface, border: `1px solid ${accent ? `${accent}55` : C.border}`,
      borderRadius: 8, padding: 20, ...style,
    }}
  >
    {children}
  </section>
);

const Overline = ({ children, Icon, color = C.body }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 7, marginBottom: 14,
      fontFamily: HEAD, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
      textTransform: "uppercase", color,
    }}
  >
    {Icon && <Icon size={14} strokeWidth={1.5} />} {children}
  </div>
);

const Btn = ({ children, onClick, variant = "primary", disabled, type = "button", Icon }) => {
  const styles = {
    primary: { bg: C.primary, fg: "#0A0A0A", bd: C.primary, glow: `0 0 10px ${C.primary}33` },
    secondary: { bg: "transparent", fg: C.ink, bd: C.border, glow: "none" },
    ghost: { bg: "transparent", fg: C.body, bd: "transparent", glow: "none" },
  }[variant];
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
        borderRadius: 6, fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        color: styles.fg, background: styles.bg, border: `1px solid ${styles.bd}`,
        boxShadow: styles.glow, opacity: disabled ? 0.45 : 1, transition: "background .15s, border-color .15s",
      }}
      onMouseEnter={(e) => { if (!disabled && variant === "secondary") e.currentTarget.style.borderColor = `${C.primary}80`; }}
      onMouseLeave={(e) => { if (variant === "secondary") e.currentTarget.style.borderColor = C.border; }}
    >
      {Icon && <Icon size={14} strokeWidth={1.5} />} {children}
    </button>
  );
};

const field = {
  width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.bg, color: C.ink, fontFamily: MONO, fontSize: 13, boxSizing: "border-box",
  outline: "none",
};
const Label = ({ children }) => (
  <label style={{ display: "block", fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 4 }}>
    {children}
  </label>
);

// ─── 50t threshold gauge ───────────────────────────────────────────────────────
function ThresholdGauge({ threshold }) {
  const mass = Number(threshold?.net_mass_t ?? 0);
  const limit = Number(threshold?.threshold_t ?? 50);
  const s = THRESH[threshold?.status] || THRESH.below;
  const pct = Math.min((mass / limit) * 100, 100);
  const warnPct = (40 / limit) * 100;
  return (
    <Card accent={s.c}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <Overline Icon={Gauge}>De-minimis · 50t / year</Overline>
        <Badge {...s} />
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <Data size={30} color={s.c}>{fmt(mass, 3)}</Data>
        <span style={{ fontFamily: MONO, fontSize: 13, color: C.muted }}>/ {fmt(limit, 0)} t CBAM goods</span>
      </div>
      <div style={{ position: "relative", height: 10, borderRadius: 999, background: C.bg, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: s.c, transition: "width .5s cubic-bezier(0.22,1,0.36,1)" }} />
        <div title="40t warning band" style={{ position: "absolute", top: -2, bottom: -2, left: `${warnPct}%`, width: 2, background: C.accent }} />
      </div>
      <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 12, color: C.body }}>
        Hydrogen &amp; electricity are excluded from the exemption mass. Warning band at{" "}
        <Data size={12} color={C.accent}>40 t</Data>.
      </div>
    </Card>
  );
}

// ─── Certificate cost forecast ──────────────────────────────────────────────────
function ForecastCard({ forecast }) {
  const noPrice = !forecast || forecast.latest_price_eur == null;
  return (
    <Card>
      <Overline Icon={Coins}>Certificate cost forecast · {REPORTING_YEAR}</Overline>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Certificate liability</div>
          <Data size={22} color={C.ink}>{fmt(forecast?.total_liability_t, 3)}</Data>
          <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}> t</span>
        </div>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Latest price</div>
          {noPrice ? (
            <span style={{ fontFamily: MONO, fontSize: 15, color: C.muted }}>— no price on record</span>
          ) : (
            <>
              <Data size={22} color={C.ink}>{eur(forecast.latest_price_eur)}</Data>
              <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{forecast.price_date} · manual</div>
            </>
          )}
        </div>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Estimated cost</div>
          {noPrice ? (
            <span style={{ fontFamily: MONO, fontSize: 15, color: C.muted }}>—</span>
          ) : (
            <Data size={22} color={C.accent}>{eur(forecast.estimated_cost_eur)}</Data>
          )}
        </div>
      </div>
      <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 6 }}>
        Quarterly holding (50% of YTD embedded)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
        {(forecast?.quarterly_holding || [1, 2, 3, 4].map((q) => ({ quarter: `Q${q}`, required_t: null }))).map((q) => (
          <div key={q.quarter} style={{ padding: "8px 10px", borderRadius: 6, background: C.bg, border: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{q.quarter}</div>
            <Data size={13} color={C.body}>{fmt(q.required_t, 3)}</Data>
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}> t</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Add-import inline form (progressive, not a modal) ──────────────────────────
function AddImportForm({ goods, onSubmit, busy }) {
  const [f, setF] = useState({ cn_code: "", origin_country: "", net_mass_t: "", import_date: "", customs_ref: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.cn_code && f.origin_country.length === 2 && Number(f.net_mass_t) > 0 && f.import_date;
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit({ ...f, net_mass_t: Number(f.net_mass_t), origin_country: f.origin_country.toUpperCase() }); }}
      style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, alignItems: "end", padding: 16, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 12 }}
    >
      <div>
        <Label>CN code (Annex I)</Label>
        <input list="cbam-cn" value={f.cn_code} onChange={set("cn_code")} placeholder="7208" style={field} required />
        <datalist id="cbam-cn">
          {goods.map((g) => <option key={g.cn_code} value={g.cn_code}>{`${g.cn_code} · ${g.sector}`}</option>)}
        </datalist>
      </div>
      <div><Label>Origin (ISO2)</Label><input value={f.origin_country} onChange={set("origin_country")} placeholder="TR" maxLength={2} style={field} required /></div>
      <div><Label>Net mass (t)</Label><input value={f.net_mass_t} onChange={set("net_mass_t")} type="number" step="0.001" min="0" placeholder="100" style={field} required /></div>
      <div><Label>Import date</Label><input value={f.import_date} onChange={set("import_date")} type="date" style={field} required /></div>
      <div><Label>Customs ref</Label><input value={f.customs_ref} onChange={set("customs_ref")} placeholder="MRN…" style={field} /></div>
      <Btn type="submit" disabled={!valid || busy} Icon={Plus}>{busy ? "Adding…" : "Add import"}</Btn>
    </form>
  );
}

// ─── Import lines table (EF provenance always visible) ──────────────────────────
function ImportLinesCard({ lines, goods, onAdd, onCsv, busy, csvResult }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const cols = ["Date", "CN", "Origin", "Net mass (t)", "Embedded (tCO₂e)", "EF provenance", "Free-alloc. basis"];
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Overline Icon={Package}>Import lines · {REPORTING_YEAR}</Overline>
        <div style={{ display: "flex", gap: 8 }}>
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => { if (e.target.files?.[0]) onCsv(e.target.files[0]); e.target.value = ""; }} />
          <Btn variant="secondary" Icon={Upload} onClick={() => fileRef.current?.click()} disabled={busy}>CSV import</Btn>
          <Btn variant={open ? "ghost" : "secondary"} Icon={open ? X : Plus} onClick={() => setOpen(!open)}>{open ? "Cancel" : "Add import"}</Btn>
        </div>
      </div>

      {open && <AddImportForm goods={goods} busy={busy} onSubmit={(v) => { onAdd(v).then((ok) => ok && setOpen(false)); }} />}
      {csvResult && (
        <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 6, background: `${C.locked}12`, border: `1px solid ${C.locked}40`, fontFamily: MONO, fontSize: 12, color: C.body }}>
          CSV: <span style={{ color: C.primary }}>{csvResult.created} created</span>
          {csvResult.errors?.length ? <span style={{ color: C.destructive }}> · {csvResult.errors.length} error(s): row {csvResult.errors.map((x) => x.row).join(", ")}</span> : null}
        </div>
      )}

      {lines.length === 0 ? (
        <EmptyState
          Icon={Package}
          title="No import lines yet"
          body="Add a CBAM good above, or bulk-import a customs CSV. Each line feeds the 50t tracker, the cost forecast, and the annual declaration."
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
            <thead>
              <tr>
                {cols.map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontFamily: SANS, fontSize: 11, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.line_id}>
                  <td style={td}><Data>{l.import_date}</Data></td>
                  <td style={td}><Data color={C.ink}>{l.cn_code}</Data></td>
                  <td style={td}><Data>{l.origin_country}</Data></td>
                  <td style={td}><Data color={C.ink}>{fmt(l.net_mass_t, 3)}</Data></td>
                  <td style={td}>{l.calc ? <Data color={C.ink}>{fmt(l.calc.embedded_total_t, 3)}</Data> : <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>pending EF</span>}</td>
                  <td style={td}><EFProvenance calc={l.calc} /></td>
                  <td style={td}>{l.calc ? <BasisBadge basis={l.calc.liability_basis} /> : <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: "4px 16px", fontFamily: SANS, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
            <span><Data size={10} color={C.body}>EF provenance</Data> — origin-country default value (RoW = rest-of-world fallback), with the mandatory Reg. 2025/2621 mark-up shown as +% (fertilisers +1%; else +10% 2026 / +20% 2027 / +30% 2028+).</span>
            <span><Data size={10} color={C.body}>Free-alloc. basis</Data> — {BASIS.exact.label}: benchmark applied directly. {BASIS.exact_conservative_route.label}: route ambiguous, lowest in-scope benchmark used (never understates). {BASIS.approximation_no_benchmark.label}: no CN benchmark, embedded × CBAM factor.</span>
          </div>
        </div>
      )}
    </Card>
  );
}
const td = { padding: "9px 10px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" };

// ─── Annual declaration workspace ───────────────────────────────────────────────
const NEXT_ACTION = {
  open: { action: "mark_draft", label: "Mark draft" },
  draft: { action: "mark_ready", label: "Mark ready" },
  ready: { action: "approve", label: "Approve & submit" },
};
function DeclarationCard({ declaration, onAction, busy }) {
  if (!declaration) return null;
  const s = DECL[declaration.status] || DECL.open;
  const next = NEXT_ACTION[declaration.status];
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Overline Icon={FileCheck2}>Annual declaration · {declaration.year}</Overline>
        <Badge {...s} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 16, marginBottom: 16 }}>
        <Metric label="Total embedded" value={fmt(declaration.total_embedded_t, 3)} unit="tCO₂e" />
        <Metric label="Certificate liability" value={fmt(declaration.total_liability_t, 3)} unit="t" />
        <Metric label="Due date" value={declaration.due_date} mono />
      </div>
      {next ? (
        <Btn Icon={ShieldCheck} disabled={busy} onClick={() => onAction(next.action)}>{busy ? "Working…" : next.label}</Btn>
      ) : (
        <Badge c={C.primary} Icon={CheckCircle2} label="Submitted — no further action" />
      )}
      <div style={{ marginTop: 10, fontFamily: SANS, fontSize: 11, color: C.muted }}>
        Approval enforces segregation of duties — the preparer cannot approve their own declaration.
      </div>
    </Card>
  );
}
const Metric = ({ label, value, unit }) => (
  <div>
    <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>{label}</div>
    <Data size={20} color={C.ink}>{value}</Data>
    {unit && <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}> {unit}</span>}
  </div>
);

// ─── Shared states ──────────────────────────────────────────────────────────────
const EmptyState = ({ Icon, title, body }) => (
  <div style={{ textAlign: "center", padding: "28px 20px", color: C.body }}>
    {Icon && <Icon size={28} strokeWidth={1.25} color={C.muted} />}
    <div style={{ fontFamily: HEAD, fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 10 }}>{title}</div>
    <div style={{ fontFamily: SANS, fontSize: 13, color: C.body, maxWidth: 460, margin: "6px auto 0", lineHeight: 1.5 }}>{body}</div>
  </div>
);
const Skeleton = ({ h = 120 }) => (
  <div style={{ height: h, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, position: "relative", overflow: "hidden" }}>
    <div className="cbam-shimmer" style={{ position: "absolute", inset: 0 }} />
  </div>
);

// ─── Readiness header ───────────────────────────────────────────────────────────
function ReadinessHeader({ readiness, entities, entityId, setEntityId, onRefresh, loading }) {
  const s = DECL[readiness?.declaration_status] || DECL.open;
  const days = readiness?.days_to_deadline;
  const dueSoon = days != null && days < 120;
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 20, background: `${C.bg}E6`, backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.border}`, padding: "16px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck size={18} strokeWidth={1.5} color={C.primary} />
        </div>
        <div>
          <div style={{ fontFamily: HEAD, fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>CBAM</div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>Carbon Border Adjustment Mechanism · definitive regime</div>
        </div>
        <div style={{ flex: 1 }} />
        <select
          value={entityId || ""} onChange={(e) => setEntityId(e.target.value)}
          style={{ ...field, width: "auto", minWidth: 180, fontFamily: SANS }}
          aria-label="Importer entity"
        >
          {entities.length === 0 && <option value="">No entities</option>}
          {entities.map((en) => <option key={en.entity_id} value={en.entity_id}>{en.name || en.entity_id}</option>)}
        </select>
        {readiness && (
          <>
            <Badge {...s} />
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 6, background: C.surface, border: `1px solid ${dueSoon ? `${C.accent}55` : C.border}` }}>
              <Clock size={13} strokeWidth={1.5} color={dueSoon ? C.accent : C.body} />
              <Data size={12} color={dueSoon ? C.accent : C.ink}>{days != null ? `${days}d` : "—"}</Data>
              <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>to {readiness.due_date}</span>
            </div>
          </>
        )}
        <Btn variant="ghost" Icon={RefreshCw} onClick={onRefresh} disabled={loading}>Refresh</Btn>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────
export default function CBAMModule() {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState(localStorage.getItem("cbam_entity_id") || "");
  const [data, setData] = useState({ readiness: null, threshold: null, forecast: null, lines: [], declaration: null });
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [csvResult, setCsvResult] = useState(null);

  // Entities + goods catalog load once.
  useEffect(() => {
    // /api/v1/enterprise/entities is the Postgres-backed list; the bare
    // /api/v1/entities route is the dead Mongo-era enterprise module (500s)
    apiFetch("/api/v1/enterprise/entities")
      .then((r) => {
        const list = r?.entities || [];
        setEntities(list);
        setEntityId((cur) => cur || list[0]?.entity_id || "");
      })
      .catch(() => setEntities([]));
    apiFetch("/api/v1/cbam/goods").then((r) => setGoods(r?.goods || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    localStorage.setItem("cbam_entity_id", entityId);
    setLoading(true); setError(null);
    const y = REPORTING_YEAR;
    const q = `entity_id=${encodeURIComponent(entityId)}&year=${y}`;
    try {
      const [readiness, threshold, forecast, linesR, declaration] = await Promise.all([
        apiFetch(`/api/v1/cbam/readiness?${q}`),
        apiFetch(`/api/v1/cbam/threshold?${q}`),
        apiFetch(`/api/v1/cbam/forecast?${q}`),
        apiFetch(`/api/v1/cbam/import-lines?${q}`),
        apiFetch(`/api/v1/cbam/declarations/${y}?entity_id=${encodeURIComponent(entityId)}`),
      ]);
      setData({ readiness, threshold, forecast, lines: linesR?.lines || [], declaration });
    } catch (e) {
      setError(e.message || "Failed to load CBAM data");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const addLine = useCallback(async (body) => {
    setBusy(true); setError(null);
    try {
      await apiFetch("/api/v1/cbam/import-lines", { method: "POST", body: { entity_id: entityId, ...body } });
      await load();
      return true;
    } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
  }, [entityId, load]);

  const csvImport = useCallback(async (file) => {
    setBusy(true); setError(null); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("entity_id", entityId);
      fd.append("file", file);
      const r = await apiFetch("/api/v1/cbam/import-lines/csv", { method: "POST", body: fd });
      setCsvResult(r);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, [entityId, load]);

  const declAction = useCallback(async (action) => {
    setBusy(true); setError(null);
    try {
      await apiFetch(`/api/v1/cbam/declarations/${REPORTING_YEAR}/status?entity_id=${encodeURIComponent(entityId)}`, { method: "POST", body: { action } });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }, [entityId, load]);

  const hasEntity = !!entityId;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.body, fontFamily: SANS }}>
      <style>{`
        @keyframes cbamShimmer { 100% { transform: translateX(100%); } }
        .cbam-shimmer { background: linear-gradient(90deg, transparent, ${C.surfaceHi}, transparent); transform: translateX(-100%); animation: cbamShimmer 1.4s infinite; }
        @media (prefers-reduced-motion: reduce) { .cbam-shimmer { animation: none; } }
      `}</style>

      <ReadinessHeader
        readiness={data.readiness} entities={entities} entityId={entityId}
        setEntityId={setEntityId} onRefresh={load} loading={loading}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 48px" }}>
        {error && (
          <div role="alert" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: `${C.destructive}14`, border: `1px solid ${C.destructive}55`, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={15} strokeWidth={1.5} color={C.destructive} />
            <span style={{ fontFamily: MONO, fontSize: 12, color: C.destructive }}>{error}</span>
          </div>
        )}

        {!hasEntity && !loading ? (
          <Card><EmptyState Icon={Package} title="Select an importer" body="Choose an entity above to see its CBAM position. Phase-1 tracks 2026 imports for above-threshold pilot importers." /></Card>
        ) : loading ? (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
              <Skeleton h={160} /><Skeleton h={160} />
            </div>
            <Skeleton h={220} /><Skeleton h={160} />
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
              <ThresholdGauge threshold={data.threshold} />
              <ForecastCard forecast={data.forecast} />
            </div>
            <ImportLinesCard
              lines={data.lines} goods={goods} onAdd={addLine} onCsv={csvImport}
              busy={busy} csvResult={csvResult}
            />
            <DeclarationCard declaration={data.declaration} onAction={declAction} busy={busy} />
          </div>
        )}
      </main>
    </div>
  );
}
